import { toast } from "sonner";
import { captureSentryException } from "@/lib/sentry";
import type { TiptapEditorRef } from "../TiptapEditor";

/**
 * Phase 7b follow-up #5 — extracted from `use-message-send.ts` so the
 * hook's `sendMessage` callback fits under the 400-LOC ceiling.
 *
 * Three things on a send-pipeline failure:
 *   1. Capture to Sentry with the channel/reply/coworker context so
 *      we can correlate failures across users.
 *   2. Restore the user's typed content into the editor (the
 *      optimistic write may have already cleared the editor in a
 *      mid-pipeline failure mode).
 *   3. Surface a toast so the user knows to retry.
 *
 * The restoration step itself can fail (editor disposed, etc.); that
 * inner failure is captured as a separate Sentry event tagged
 * `message-send-restore`.
 */
export interface HandleSendErrorContext {
  channelId: string;
  isDM: boolean;
  hasReplyParent: boolean;
  hasAttachments: boolean;
  isCoworkerDm: boolean;
}

export function handleSendError(
  err: unknown,
  contentToRestore: string,
  editorRef: { current: TiptapEditorRef | null },
  ctx: HandleSendErrorContext,
): void {
  console.error("[message-input] send failed:", err);
  captureSentryException(err, {
    tag: "message-send",
    extra: {
      channelId: ctx.channelId,
      isDM: ctx.isDM,
      hasReplyParent: ctx.hasReplyParent,
      hasAttachments: ctx.hasAttachments,
      isCoworkerDm: ctx.isCoworkerDm,
    },
  });
  try {
    editorRef.current?.setContentFromMarkdown(contentToRestore);
  } catch (restoreErr) {
    console.warn(
      "[message-input] could not restore content after send failure:",
      restoreErr,
    );
    captureSentryException(restoreErr, { tag: "message-send-restore" });
  }
  toast.error("Couldn't send — your message has been restored. Try again.");
}
